import { z } from "zod";
import { LessonSchema } from "./lesson-schema";

export const SessionStatusSchema = z.enum([
  "created",
  "prewarming",
  "ready",
  "narrating",
  "yielding",
  "interrupting",
  "listening",
  "answering",
  "resuming",
  "paused",
  "recovering",
  "complete",
  "failed",
]);
export const CursorSchema = z
  .object({
    phraseId: z.string().nullable(),
    phraseIndex: z.number().int().nonnegative(),
    textOffset: z.number().int().nonnegative(),
  })
  .readonly();
export const AnswerSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    status: z.enum(["generating", "playing", "complete"]),
  })
  .readonly();
export const ReentryPlanSchema = z
  .object({
    class: z.enum(["direct", "phrase-restoration", "micro-recap"]),
    phraseId: z.string().nullable(),
    replayStartOffset: z.number().int().nonnegative(),
    firstUnplayedOffset: z.number().int().nonnegative(),
    owner: z.enum(["course-pack", "live"]),
    bridgeText: z.string().min(1).nullable().default(null),
    bridgeId: z.string().min(1).nullable().default(null),
    policyVersion: z.string().min(1).nullable().default(null),
    reason: z.string().min(1).default("direct-continuation"),
    leadInDurationMs: z.number().finite().nonnegative().max(5000).nullable(),
    visualHandle: z
      .object({
        mediaTimeMs: z.number().finite().nonnegative(),
        segmentKey: z.string().min(1),
        segmentId: z.string().min(1).optional(),
      })
      .readonly()
      .nullable(),
    requiresAlignment: z.boolean(),
  })
  .refine(
    (plan) =>
      plan.requiresAlignment || (plan.leadInDurationMs !== null && plan.visualHandle !== null),
    {
      message: "Measured re-entry requires a measured lead-in and a verified visual handle.",
      path: ["requiresAlignment"],
    },
  )
  .refine(
    (plan) =>
      (plan.class === "micro-recap") ===
      (plan.bridgeText !== null && plan.bridgeId !== null && plan.policyVersion !== null),
    {
      message: "A micro-recap requires exactly one approved, versioned bridge sentence.",
      path: ["bridgeText"],
    },
  )
  .readonly();
export const SessionSchema = z
  .object({
    schemaVersion: z.literal(1),
    id: z.string().min(1),
    lesson: LessonSchema,
    mode: z.enum(["hybrid", "all-live"]),
    status: SessionStatusSchema,
    generationEpoch: z.number().int().nonnegative(),
    bridgeFence: z.number().int().nonnegative(),
    turnId: z.string().min(1),
    providerPath: z.string().min(1),
    speechOwner: z.enum(["none", "course-pack", "live-avatar", "audio-only", "text-preview"]),
    committedCursor: CursorSchema,
    interruptionAnchor: CursorSchema.nullable(),
    candidate: z
      .object({
        onsetId: z.string(),
        previousStatus: z.enum(["narrating", "answering"]),
        anchor: CursorSchema,
        provisionalAnchor: CursorSchema.optional(),
        /**
         * Where in the answer's text the clause the learner was hearing starts, when the onset
         * came over a finished answer: the answer is stopped for the candidate and, if the onset
         * is disproved, spoken again from here.
         */
        answerOffset: z.number().int().nonnegative().optional(),
      })
      .readonly()
      .nullable(),
    question: z.string().nullable(),
    answer: AnswerSchema.nullable(),
    reentryPlan: ReentryPlanSchema.nullable(),
    lastBridgeId: z.string().min(1).nullable().default(null),
    recoveryReason: z.string().nullable(),
    resumeStatus: SessionStatusSchema.nullable(),
    processedEventIds: z.array(z.string()).readonly(),
    confirmedOnsetIds: z.array(z.string()).readonly(),
    revision: z.number().int().nonnegative(),
  })
  .readonly();

const envelope = {
  eventId: z.string().min(1).max(200),
  sessionId: z.string().min(1),
  turnId: z.string().min(1),
  generationEpoch: z.number().int().nonnegative(),
  bridgeFence: z.number().int().nonnegative(),
  providerPath: z.string().min(1),
  clauseId: z.string().nullable(),
  at: z.string().datetime(),
};
const inputSource = z.enum(["microphone", "ask", "text"]);
export const SessionActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("prewarm") }),
  z.object({ type: z.literal("ready") }),
  z.object({ type: z.literal("start") }),
  z.object({ type: z.literal("pause") }),
  z.object({ type: z.literal("resume") }),
  z.object({ type: z.literal("stopped") }),
  z.object({ type: z.literal("recovered") }),
  z.object({ type: z.literal("refresh-output") }),
  z.object({ type: z.literal("reset") }),
  z.object({ type: z.literal("end") }),
  z.object({ type: z.literal("producer-complete") }),
  z.object({
    type: z.literal("playback"),
    phraseId: z.string(),
    textOffset: z.number().int().nonnegative(),
    completed: z.boolean(),
    playbackConfirmed: z.literal(true),
    mediaTimeMs: z.number().finite().nonnegative().optional(),
    segmentId: z.string().min(1).optional(),
  }),
  z.object({
    type: z.literal("yield"),
    onsetId: z.string().min(1),
    inputSource,
    authorized: z.boolean(),
    /** The start of the answer clause the learner was hearing at the onset (see `candidate`). */
    answerOffset: z.number().int().nonnegative().optional(),
  }),
  z.object({ type: z.literal("reject-onset"), onsetId: z.string().min(1) }),
  z.object({
    type: z.literal("interrupt"),
    onsetId: z.string().min(1),
    inputSource,
    authorized: z.boolean(),
  }),
  z.object({ type: z.literal("question"), text: z.string().trim().min(1).max(4000) }),
  z.object({
    type: z.literal("answer-start"),
    answerId: z.string().min(1),
    text: z.string().trim().min(1).max(12000),
  }),
  z.object({
    type: z.literal("answer-complete"),
    answerId: z.string().min(1),
    playbackConfirmed: z.literal(true),
  }),
  z.object({ type: z.literal("recover"), reason: z.string().min(1).max(500) }),
  z.object({ type: z.literal("replace-provider"), nextProviderPath: z.string().min(1) }),
  z.object({ type: z.literal("fail"), reason: z.string().min(1).max(500) }),
]);
export const SessionEventSchema = z.intersection(z.object(envelope), SessionActionSchema);
export type Session = z.infer<typeof SessionSchema>;
export type SessionStatus = z.infer<typeof SessionStatusSchema>;
export type SessionAction = z.infer<typeof SessionActionSchema>;
export type SessionEvent = z.infer<typeof SessionEventSchema>;
export type Cursor = z.infer<typeof CursorSchema>;
export type ReentryPlan = z.infer<typeof ReentryPlanSchema>;
export type SessionEffect = {
  readonly type: "cancel-output" | "clear-preroll" | "release-resources";
  readonly sessionId: string;
  readonly turnId: string;
  readonly clauseId: string | null;
  readonly providerPath: string;
  readonly at: string;
  readonly generationEpoch: number;
  readonly bridgeFence: number;
};
export type TransitionResult = {
  readonly session: Session;
  readonly accepted: boolean;
  readonly effects: readonly SessionEffect[];
  readonly reason:
    | "applied"
    | "duplicate"
    | "stale_epoch"
    | "stale_fence"
    | "invalid_transition"
    | "invalid_playback"
    | "wrong_session"
    | "wrong_turn"
    | "wrong_route"
    | "wrong_clause"
    | "unauthorized";
};
